import { useCallback, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { clockApi } from "../../../api/clock";
import { PageHeader } from "../../../components/ComingSoon";
import { LoadState, useLoad } from "../shared";

function inline(text: string): ReactNode[] {
  return text.split(/(`[^`]+`|\*\*[^*]+\*\*)/g).map((p, i) =>
    p.startsWith("`") ? <code key={i} className="rounded-sm bg-ink px-1 font-mono text-[12px] text-zinc-100">{p.slice(1, -1)}</code>
      : p.startsWith("**") ? <strong key={i} className="text-white">{p.slice(2, -2)}</strong> : p);
}

function Markdown({ md }: { md: string }) {
  const out: ReactNode[] = [];
  const lines = md.split("\n");
  for (let i = 0; i < lines.length; i++) {
    const l = lines[i];
    if (l.startsWith("|")) {
      const rows: string[][] = [];
      while (i < lines.length && lines[i].startsWith("|")) {
        if (!/^\|[-| ]+\|$/.test(lines[i])) rows.push(lines[i].split("|").slice(1, -1).map((c) => c.trim()));
        i++;
      }
      i--;
      out.push(<div key={i} className="my-4 overflow-x-auto"><table className="w-full text-left text-xs"><tbody>{rows.map((r, ri) => (
        <tr key={ri} className="border-b border-white/10">{r.map((c, ci) => ri === 0 ? <th key={ci} className="px-3 py-2 text-zinc-400">{c}</th> : <td key={ci} className="px-3 py-2 align-top text-zinc-300">{inline(c)}</td>)}</tr>
      ))}</tbody></table></div>);
    } else if (l.startsWith("# ")) out.push(<h1 key={i} className="hidden">{l.slice(2)}</h1>);
    else if (l.startsWith("## ")) out.push(<h2 key={i} className="mb-3 mt-8 font-display text-xl font-bold uppercase tracking-tight text-white">{l.slice(3)}</h2>);
    else if (/^\s*([-*]|\d+\.) /.test(l)) out.push(<p key={i} className={`my-1 text-sm text-zinc-300 ${/^\s{2,}/.test(l) ? "pl-10" : "pl-4"}`}>{l.trim().startsWith("- [ ]") ? "☐ " + l.trim().slice(6) : l.trim().replace(/^- /, "• ")}</p>);
    else if (l.trim()) out.push(<p key={i} className="my-2 text-sm text-zinc-300">{inline(l)}</p>);
  }
  return <>{out}</>;
}

export default function TwilioGuide() {
  const loader = useCallback(() => clockApi.guide(), []);
  const { data, error } = useLoad(loader);
  return (
    <section data-testid="twilio-guide-page" className="animate-rise">
      <Link to="/admin/settings" className="mb-4 inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-white" data-testid="twilio-guide-back-link"><ArrowLeft className="h-3.5 w-3.5" /> Settings</Link>
      <PageHeader title="Twilio Setup Guide" />
      <LoadState error={error} loading={!data && !error} testId="twilio-guide-load" />
      {data && <article data-testid="twilio-guide-content" className="max-w-4xl rounded-sm border border-white/10 bg-surface p-6"><Markdown md={data.markdown} /></article>}
    </section>
  );
}
